import { ChangeDetectorRef, Component, NgZone, OnInit, inject } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import axios from 'axios';
import { Router } from '@angular/router';
import { UserService } from 'src/app/services/user/user.service';
import { Api } from './services/api/api.service';
import { environment } from '@environments/environment';
import { SocketService } from './services/socket/socket.service';
import { NotificationService } from './services/notification/notification.service';
import * as _ from 'lodash';
import { FridgeService } from 'src/app/services/fridge/fridge.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private platform = inject(Platform);
  private splashScreen = inject(SplashScreen);
  private statusBar = inject(StatusBar);
  private router = inject(Router);
  userService = inject(UserService);
  private modalController = inject(ModalController);
  notificationService = inject(NotificationService);
  private socketService = inject(SocketService);
  private fridgeService = inject(FridgeService);
  translate = inject(TranslateService);
  private ngZone = inject(NgZone);
  private cdRef = inject(ChangeDetectorRef);

  private authFailureInProgress = false;

  constructor() {
    const translate = this.translate;

    this.initializeApp();

    translate.addLangs(['en', 'de']);
    translate.setDefaultLang('en');

    const browserLang = translate.getBrowserLang() ?? 'de';
    const finalLanguage = browserLang.match(/en|de/) ? browserLang : 'de';
    const storedLang = localStorage.getItem('serviceman_lang');
    if (storedLang) {
      translate.use(storedLang);
    } else {
      translate.use(finalLanguage);
      localStorage.setItem('serviceman_lang', finalLanguage);
    }
  }

  loading = false;
  socketSubscription: any;

  initializeApp() {
    axios.defaults.baseURL = environment.apiUrl;
    axios.defaults.withCredentials = true;

    axios.interceptors.request.use(async (config) => {
      if (this.shouldSkipAuthRefresh(config)) {
        return config;
      }
      try {
        await this.userService.ensureValidAccessToken();
      } catch (error) {
        await this.handleAuthFailure();
        return Promise.reject(error);
      }
      return config;
    });

    axios.interceptors.response.use(
      (response) => this.ngZone.run(() => response.data),
      async (error) => this.ngZone.run(async () => {
        if (error.response?.data?.data?.length > 0) {
          if (!this.shouldSuppressError(error)) {
            await this.notificationService.notifyError(error.response.data.data[0].message);
          }
        } else if (error.response?.data?.data?.error?.message) {
          if (!this.shouldSuppressError(error)) {
            await this.notificationService.notifyError(error.response.data.data.error.message);
          }
        } else if (error.response?.data?.message) {
          if (!this.shouldSuppressError(error)) {
            await this.notificationService.notifyError(error.response?.data?.message);
          }
        } else {
          if (!this.shouldSuppressError(error)) {
            await this.notificationService.notifyError(error.message || error);
          }
        }

        if (error.response?.status === 401 && !this.shouldSuppressError(error)) {
          await this.handleAuthFailure();
        }
        return Promise.reject(error);
      }),
    );
  }

  async ngOnInit() {
    this.userService.userLoaded.subscribe(async () => {
      this.loading = true;
      this.cdRef.detectChanges();
      if (this.userService.isAuthenticated) {
        this.ensureSocketConnection();
        await this.getGlobalData();
      }
      this.loading = false;
      this.cdRef.detectChanges();
    });
    this.userService.authSyncEvent.subscribe(async (type) => {
      if (type === 'logout') {
        const isModalOpened = await this.modalController.getTop();
        if (isModalOpened) {
          await this.modalController.dismiss();
        }
        await this.router.navigate(['/login']);
        this.loading = false;
        return;
      }
      if (type === 'login') {
        try {
          await this.userService.ensureUserLoaded();
        } catch (e) {
          return;
        }
        const path = window.location?.pathname || '';
        if (path.includes('/login')) {
          await this.router.navigate(['/home']);
        }
      }
    });
    this.loading = false;
  }

  public async getGlobalData() {
    // groups only order the fridge list, so a failure there must not break loading
    Api.getAccessibleMerchantGroups()
      .then(({ merchantGroups }) => this.userService.setMerchantGroups(merchantGroups || []))
      .catch((err) => console.error('Failed to load merchant groups', err));
    const { merchants } = await Api.getMerchants();
    this.userService.setMerchants(merchants);
    await this.getFridges();

    if (this.socketService.socket?.connected) {
      this.joinStatusRoom(true);
    }
    if (!this.socketSubscription) {
      this.socketSubscription = this.socketService.getSocketChangeEmitter().subscribe((status) => {
        this.joinStatusRoom(status);
        if (status) {
          this.getFridges();
        }
      });
    }
  }

  async getFridges() {
    const fridges = await Api.getAllFridges();
    this.fridgeService.setFridges(fridges);
  }

  joinStatusRoom(status: boolean) {
    if (!status) return;
    _.map(this.fridgeService.fridges, (fridge) => {
      this.socketService.joinRoom(fridge._id, 'devicesFridgeStatus');
    });
  }

  private ensureSocketConnection() {
    if (this.socketService.socket?.connected) {
      return;
    }
    this.socketService.setupSocketConnection();
  }

  private async handleAuthFailure(): Promise<void> {
    if (this.authFailureInProgress) {
      return;
    }
    this.authFailureInProgress = true;
    this.userService.logoutUser(false, true);
    const isModalOpened = await this.modalController.getTop();
    if (isModalOpened) {
      await this.modalController.dismiss();
    }
    await this.router.navigate(['/login']);
    this.loading = false;
  }

  private shouldSkipAuthRefresh(config: any): boolean {
    const url: string = config?.url || '';
    return (
      url.includes('/auth/bo/login') ||
      url.includes('/auth/boLogin') ||
      url.includes('/auth/bo/refresh') ||
      url.includes('/auth/bo/logout')
    );
  }

  private shouldSuppressError(error: any): boolean {
    const rawMessage = error?.message || error?.toString?.() || '';
    if (rawMessage === 'auth_failed' || rawMessage === 'auth_blocked') {
      return true;
    }
    // merchant groups only order the fridge list; a user without access to them must not see an error
    if ((error?.config?.url || '').includes('/merchantGroup/accessible') && error?.response?.status !== 401) {
      return true;
    }
    // Suppress noise when unauthenticated on /login doing a cookie check
    if (error?.response?.status === 401) {
      const url: string = error?.config?.url || '';
      const path = window.location?.pathname || '';
      if (url.includes('/auth/bo/refresh')) {
        return true;
      }
      if (url.includes('/user') && path.includes('/login')) {
        return true;
      }
    }
    return false;
  }
}
